import { FormApi } from "final-form";
import _ from "lodash";
import { Form } from "react-final-form";
import { useTranslation } from "react-i18next";
import i18n from "src/i18n/config";
import { useUpdateProductMutation } from "src/ui/containers/dashboard/products/api";
import { Modal } from "src/ui/shared/Modal/Modal";
import { useValidationSchema } from "src/ui/utils/useValidationSchema";
import { makeStyles } from "tss-react/mui";
import { z } from "zod";

import { useAllCurrentWorkspaceFiles } from "@dashboard/files/api/helpers";
import { FileContentType } from "@dashboard/files/types";
import { createSource } from "@dashboard/products/components/ProductBuilder/components/Recipes";
import { AndroidModule, PrebuiltBootAnimationModule, Product, Sources } from "@dashboard/products/types";
import { useCurrentWorkspace } from "@dashboard/workspaces/api/helpers";
import { PermissionService } from "@services/permissionService/permissionService";
import { PrimaryButton } from "@shared/CustomButton";
import { ControlledSelectField } from "@shared/form/SelectField/SelectField";
import { Tooltip } from "@shared/Tooltip";

import SharedUtils from "./ModulePanel.utils";

const schema = z.object({
    fileId: z.string({ message: i18n.t("common:validation.fieldRequired") }),
});

const useStyles = makeStyles()((theme) => ({
    form: { display: "flex", flexDirection: "column", gap: theme.spacing(2), marginTop: theme.spacing(3) },
}));

type FormValues = {
    fileId: string;
};

type Props = {
    product: Product | null;
    open: boolean;
    onToggle: (value: boolean) => void;
};

const contentTypes = [
    FileContentType.ZipCompressedV2,
    FileContentType.Zip,
    FileContentType.ZipMultipart,
    FileContentType.ZipCompressed,
];

// createSource reuses an existing source id when the picked file already has one, so swapping a file can
// legitimately land on the id we are about to drop. Keep the entry unless nothing references it any more.
const dropUnreferencedSource = (
    sources: Sources,
    modules: AndroidModule[],
    previousSourceId: string | undefined,
    sourceId: string,
) => {
    if (!previousSourceId || previousSourceId === sourceId || modules.some((item) => item.sourceId === previousSourceId)) {
        return sources;
    }

    const remainingSources = { ...sources };
    delete remainingSources[previousSourceId];
    return remainingSources;
};

export const BootAnimationDialog = (props: Props) => {
    const { classes } = useStyles();
    const { t } = useTranslation(["pages", "modals", "common", "sidepanels"]);
    const validate = useValidationSchema(schema);

    const [updateProduct] = useUpdateProductMutation();

    const modules: AndroidModule[] = props.product?.customBuildConfig?.modules || [];
    const sources = props.product?.customBuildConfig?.sources || {};

    const currentModule = modules.find((item): item is PrebuiltBootAnimationModule => item.type === "prebuilt_bootanimation");
    const currentSourceId = currentModule?.sourceId;
    const currentFileId = currentSourceId ? sources[currentSourceId]?.fileId : undefined;

    const { data: workspace } = useCurrentWorkspace();
    const { data, isLoading, isError } = useAllCurrentWorkspaceFiles({ contentTypes }, { skip: !props.open });

    const accessibleFiles = data.filter(
        (item) => item.id === currentFileId || !SharedUtils.checkFileSourceExists(modules, sources, item.id),
    );
    const fileOptions = _.orderBy(
        accessibleFiles.map((item) => ({ label: item.filename, value: item.id })),
        "label",
    );

    // Falling back to the first file is right when adding, but on the edit path it would silently swap an
    // animation whose file has since been deleted from the workspace — the card still shows the stored
    // filename, so nothing warns the user. Leave the field empty and let validation demand a deliberate pick.
    const initialFileId = currentModule
        ? fileOptions.find((option) => option.value === currentFileId)?.value
        : fileOptions[0]?.value;

    const { productAbility } = PermissionService();
    const cannotManageProducts = productAbility(workspace).cannot("manage", "Product");
    const formDisabled = isLoading || !fileOptions.length || cannotManageProducts || !props.product;

    const onSubmit = async (values: FormValues, form: FormApi<FormValues>) => {
        const file = data.find((item) => item.id === values.fileId);
        if (file && props.product) {
            const [sourceId, updatedSources] = createSource(file, sources);

            const newProduct = { ...props.product };
            if (newProduct.customBuildConfig) {
                const updatedModules: AndroidModule[] = currentModule
                    ? modules.map((item) => (item === currentModule ? { ...item, sourceId } : item))
                    : [...modules, { type: "prebuilt_bootanimation", name: "boot_animation", sourceId, tags: ["Own"] }];

                newProduct.customBuildConfig = {
                    ...newProduct.customBuildConfig,
                    modules: updatedModules,
                    sources: dropUnreferencedSource(updatedSources, updatedModules, currentSourceId, sourceId),
                };
            }
            const saved = await SharedUtils.saveProduct(updateProduct, newProduct);
            if (!saved) {
                return;
            }
        }
        form.restart();
        props.onToggle(false);
    };

    const getFilesUnavailableMessage = () => {
        if (isLoading) {
            return undefined;
        }
        if (isError) {
            return t("common:error.files.failedToFetchFiles");
        }
        if (!fileOptions.length) {
            return t("modals:manageProductModules.bootAnimation.noFilesAvailable");
        }
        return undefined;
    };

    const getTooltipTitle = () => {
        const filesUnavailable = getFilesUnavailableMessage();
        if (filesUnavailable) {
            return filesUnavailable;
        }
        if (cannotManageProducts) {
            return t("common:tooltip.cannotManageProducts");
        }
        if (!props.product) {
            return t("common:tooltip.noProductSelected");
        }
        return undefined;
    };

    const tooltipTitle = getTooltipTitle();

    return (
        <Form<FormValues>
            onSubmit={onSubmit}
            validate={validate}
            initialValues={{
                fileId: initialFileId,
            }}
            render={({ handleSubmit, form, submitting }) => (
                <Modal
                    open={props.open}
                    onClose={() => {
                        form.restart();
                        props.onToggle(false);
                    }}
                    title={t("modals:manageProductModules.bootAnimation.title")}
                    endButton={
                        <Tooltip hide={!tooltipTitle} title={tooltipTitle}>
                            <PrimaryButton onClick={form.submit} disabled={submitting || formDisabled} loading={submitting}>
                                {t("common:button.save")}
                            </PrimaryButton>
                        </Tooltip>
                    }
                    description={t("modals:manageProductModules.bootAnimation.description")}
                >
                    <form onSubmit={handleSubmit} className={classes.form}>
                        <ControlledSelectField
                            name="fileId"
                            options={fileOptions}
                            disabled={formDisabled}
                            loading={isLoading}
                            label={getFilesUnavailableMessage() ?? t("common:label.file")}
                        />
                    </form>
                </Modal>
            )}
        />
    );
};
